import { usePermission } from '@/lib/permissions/use-permission';
import type { PermissionStatus } from '@/lib/permissions/types';

/** Per-permission status as reported by the unified `check_permission` command
 *  (camelCase). */
export type PermStatus = PermissionStatus;

export interface RecordingPermissions {
  /** Microphone permission status, or `null` until the first probe resolves. */
  microphone: PermStatus | null;
  /** Screen-recording permission status, or `null` until the first probe. */
  screenRecording: PermStatus | null;
  /** `true` when mic is granted OR not yet probed (`null`) — so the UI never
   *  flashes a warning before the first probe or on a transient error. */
  micGranted: boolean;
  /** `true` when screen recording is granted OR not yet probed. */
  screenGranted: boolean;
  /** Re-run the probe on demand. */
  refresh: () => Promise<void>;
}

/**
 * Read-only probe of the macOS recording permissions (microphone + screen
 * recording) for the Settings page, so each device selector can show an
 * inline "permission missing" warning that reflects the live OS state.
 *
 * Migrated in M5 to use `usePermission` (react-query 1s poll, unified
 * check_permission command). The bespoke `get_permission_status` IPC call,
 * `setInterval`, and window-focus listener are deleted — react-query handles
 * polling and visibility-based refetch automatically.
 *
 * Deliberately read-only: unlike `use-permission-flow` (the record-time flow
 * machine with explainer states + restart escalation), this hook only reads
 * status. Accessibility is handled by `useAccessibilityPermissionFlow`.
 *
 * On non-macOS `check_permission` reports `granted` (there is no TCC-equivalent
 * gate to query), so no warnings surface off macOS.
 */
export function useRecordingPermissions(): RecordingPermissions {
  const mic = usePermission('microphone');
  const screen = usePermission('screenRecording');

  const microphone: PermStatus | null = mic.status ?? null;
  const screenRecording: PermStatus | null = screen.status ?? null;

  // null (not yet probed) reads as granted so no warning flashes on first paint.
  const micGranted = microphone === null || microphone === 'granted';
  const screenGranted = screenRecording === null || screenRecording === 'granted';

  const refresh = async (): Promise<void> => {
    await mic.refetch();
    await screen.refetch();
  };

  return { microphone, screenRecording, micGranted, screenGranted, refresh };
}
